import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Printer, Search } from 'lucide-react';
import Button from '@/components/ui/Button';
import AutocompleteSelect from '@/components/ui/AutocompleteSelect';
import { useUsers } from '@/hooks';
import { useCreditSalesReport } from '@/hooks/useCreditHistory';
import { CreditHistory, CreditOperationType, PricePer12CreditsCurrency } from '@/types/model';

const operationLabelMap: Record<CreditOperationType, string> = {
  ADD_CREDITS: 'Agregar',
  ADD_DEMO_CREDITS: 'Agregar (Demo)',
  DEDUCT_CREDITS: 'Quitar',
  ADD_CREDITS_FOR_SALE: 'Agregar (Venta)',
  DEDUCT_CREDITS_FOR_SALE: 'Quitar (Venta)',
  REFUND_CREDITS: 'Reembolsar',
  REFUND_CREDITS_FOR_SALE: 'Reembolsar (Venta)',
};

const formatReportDateTime = (isoDateString?: string) => {
  if (!isoDateString) return '';

  const hasTimeZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(isoDateString);
  const normalizedDateString = hasTimeZone ? isoDateString : `${isoDateString}Z`;
  const date = new Date(normalizedDateString);

  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);

  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day} ${values.hour}:${values.minute} ${values.dayPeriod}`;
};

const formatReportCurrency = (amount: number, currency?: PricePer12CreditsCurrency) => {
  const prefix = currency === 'DOP' ? 'RD$ ' : 'US$ ';
  return `${prefix}${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatInputDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const getCurrentMonthDateRange = () => {
  const now = new Date();

  return {
    dateFrom: formatInputDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    dateTo: formatInputDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

const CreditSalesReport = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentMonthDateRange = useMemo(() => getCurrentMonthDateRange(), []);
  const [filters, setFilters] = useState({
    performedByUserId: searchParams.get('performedByUser.id') ?? '',
    dateFrom: searchParams.get('dateFrom') ?? currentMonthDateRange.dateFrom,
    dateTo: searchParams.get('dateTo') ?? currentMonthDateRange.dateTo,
  });

  useEffect(() => {
    setFilters({
      performedByUserId: searchParams.get('performedByUser.id') ?? '',
      dateFrom: searchParams.get('dateFrom') ?? currentMonthDateRange.dateFrom,
      dateTo: searchParams.get('dateTo') ?? currentMonthDateRange.dateTo,
    });
  }, [currentMonthDateRange, searchParams]);

  const { data: sellersData } = useUsers({ "role.code": 'agent', _size: 1000 });

  const performedByOptions = useMemo(
    () =>
      (sellersData?.content ?? []).map((seller) => ({
        value: String(seller.id),
        label: seller.fullName,
      })),
    [sellersData]
  );

  const selectedUserLabel = useMemo(
    () => performedByOptions.find((option) => option.value === filters.performedByUserId)?.label ?? 'Todos',
    [filters.performedByUserId, performedByOptions]
  );

  const reportParams = useMemo(() => ({
    'performedByUser.id': searchParams.get('performedByUser.id') ?? undefined,
    dateFrom: searchParams.get('dateFrom') ?? undefined,
    dateTo: searchParams.get('dateTo') ?? undefined,
  }), [searchParams]);

  const canGenerate = Boolean(
    reportParams['performedByUser.id'] &&
    reportParams.dateFrom &&
    reportParams.dateTo
  );

  const reportQuery = useCreditSalesReport(reportParams, canGenerate);

  const handleGenerateReport = () => {
    const nextParams = new URLSearchParams();

    if (filters.performedByUserId) {
      nextParams.set('performedByUser.id', filters.performedByUserId);
    }
    if (filters.dateFrom) {
      nextParams.set('dateFrom', filters.dateFrom);
    }
    if (filters.dateTo) {
      nextParams.set('dateTo', filters.dateTo);
    }

    setSearchParams(nextParams, { replace: true });
  };

  const handlePrint = () => {
    window.print();
  };

  const renderOperationAmount = (operation: CreditHistory) => {
    const isSale =
      operation.operationType === 'ADD_CREDITS' ||
      operation.operationType === 'ADD_CREDITS_FOR_SALE';
    return `${isSale ? '+' : '-'}${operation.amount}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <style>{`
        @media print {
          html,
          body,
          #root,
          #root > div,
          .app-content {
            height: auto !important;
            min-height: 0 !important;
            overflow: visible !important;
          }

          nav {
            display: none !important;
          }

          .print-hidden {
            display: none !important;
          }

          body {
            background: #fff !important;
          }

          .print-surface {
            box-shadow: none !important;
            border: 0 !important;
            overflow: visible !important;
            display: block !important;
          }

          .print-table-wrapper {
            overflow: visible !important;
          }

          .print-meta-grid {
            display: grid !important;
            grid-template-columns: repeat(6, minmax(100px, 1fr)) !important;
            gap: 12px !important;
            align-items: start !important;
          }

          .print-meta-title {
            grid-column: 1 / -1 !important;
          }

          .print-table {
            font-size: 10px;
            width: 100% !important;
          }

          .print-table th,
          .print-table td {
            padding: 4px 5px !important;
            white-space: nowrap !important;
          }

          .print-table .print-wrap {
            white-space: normal !important;
            line-height: 1.2 !important;
          }
        }
      `}</style>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="print-hidden mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reporte de ventas de créditos</h1>
            <p className="text-sm text-gray-500">Genera un reporte imprimible por usuario y rango de fechas.</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/credits-history">
              <Button variant="outline" size="sm">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver
              </Button>
            </Link>
            <Button variant="primary" size="sm" onClick={handlePrint} disabled={!reportQuery.data}>
              <Printer className="mr-2 h-4 w-4" />
              Imprimir
            </Button>
          </div>
        </div>

        <div className="print-hidden mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(260px,1fr)_180px_180px_auto] md:items-end">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Realizado por</label>
              <AutocompleteSelect
                value={filters.performedByUserId}
                onChange={(value) => {
                  setFilters((current) => ({ ...current, performedByUserId: value }));
                }}
                options={performedByOptions}
                placeholder="Selecciona un usuario"
                size="sm"
                className="text-base"
                allowClear
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Desde</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(event) => setFilters((current) => ({ ...current, dateFrom: event.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Hasta</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(event) => setFilters((current) => ({ ...current, dateTo: event.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div>
              <Button size="sm" onClick={handleGenerateReport} disabled={!filters.performedByUserId || !filters.dateFrom || !filters.dateTo}>
                <Search className="mr-2 h-4 w-4" />
                Generar reporte
              </Button>
            </div>
          </div>
        </div>

        {!canGenerate && (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500 print-hidden">
            Selecciona un usuario y un rango de fechas para generar el reporte.
          </div>
        )}

        {canGenerate && reportQuery.isLoading && (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500 print-hidden">
            Generando reporte...
          </div>
        )}

        {canGenerate && reportQuery.isError && (
          <div className="rounded-lg border border-red-200 bg-white p-8 text-center text-sm text-red-600 print-hidden">
            {reportQuery.error?.message || 'No fue posible generar el reporte.'}
          </div>
        )}

        {reportQuery.data && (
          <div className="print-surface rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-4 py-4">
              <div className="print-meta-grid grid grid-cols-1 gap-3 lg:grid-cols-[minmax(220px,1.4fr)_repeat(6,minmax(100px,0.75fr))] lg:items-start lg:gap-4">
                <div className="print-meta-title min-w-0">
                  <h2 className="text-xl font-semibold text-gray-900">Reporte de ventas de créditos</h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Generado el {formatReportDateTime(reportQuery.data.generatedAt)}
                  </p>
                </div>
                <div className="min-w-0 text-xs text-gray-600">
                    <div className="font-medium text-gray-900">Realizado por</div>
                    <div className="truncate">{selectedUserLabel}</div>
                </div>
                <div className="min-w-0 text-xs text-gray-600">
                    <div className="font-medium text-gray-900">Desde</div>
                    <div>{reportQuery.data.dateFrom || '-'}</div>
                </div>
                <div className="min-w-0 text-xs text-gray-600">
                    <div className="font-medium text-gray-900">Hasta</div>
                    <div>{reportQuery.data.dateTo || '-'}</div>
                </div>
                <div className="min-w-0 text-xs text-gray-600">
                    <div className="font-medium text-gray-900">Créditos netos</div>
                    <div className="text-xs font-semibold text-emerald-600">{reportQuery.data.netCreditsSold}</div>
                </div>
                <div className="min-w-0 text-xs text-gray-600">
                    <div className="font-medium text-gray-900">Usuarios</div>
                    <div className="text-xs font-semibold text-gray-900">{(reportQuery.data.netCreditsSold / 12).toFixed(2)}</div>
                </div>
                <div className="min-w-0 text-xs text-gray-600">
                    <div className="font-medium text-gray-900">Precio x Usuario</div>
                    <div className="text-xs font-semibold text-gray-900">
                      {formatReportCurrency(
                        reportQuery.data.pricePer12Credits,
                        reportQuery.data.pricePer12CreditsCurrency
                      )}
                    </div>
                </div>
                <div className="min-w-0 text-xs text-gray-600">
                    <div className="font-medium text-gray-900">Monto a pagar</div>
                    <div className="text-xs font-semibold text-emerald-600">
                      {formatReportCurrency(
                        reportQuery.data.totalAmountDue,
                        reportQuery.data.pricePer12CreditsCurrency
                      )}
                    </div>
                </div>
              </div>
            </div>

            <div className="print-table-wrapper overflow-x-auto">
              <table className="print-table min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Usuario objetivo</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Operación</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Cantidad</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {reportQuery.data.operations.map((operation) => (
                    <tr key={operation.id}>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatReportDateTime(operation.operationDate)}</td>
                      <td className="print-wrap px-4 py-3 text-sm text-gray-900">
                        <div className="font-medium">{operation.targetUser?.username || '(Usuario eliminado)'}</div>
                        <div className="text-xs text-gray-500">{operation.targetUser?.fullName || '(Usuario eliminado)'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{operationLabelMap[operation.operationType]}</td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">{renderOperationAmount(operation)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditSalesReport;
